import { useState, useEffect } from 'react';
import { FileWithPath } from 'react-dropzone';
import { ClientError, APIError, useQueryClient, useLoaderToast, useJobStatus } from 'shared-ui';
import { toast } from 'react-toastify';

import { ValidateOwnersImportFileErrorResponse } from 'api/owners/owners.types';
import { useMutation } from 'hooks/useMutation/useMutation';
import { useLocale } from 'hooks/useLocale/useLocale';
import { addOwnersFromFile, validateOwnersImportFile } from 'api/owners/owners';
import { AppMessages } from 'i18n/messages';
import { QueryKeyEnum } from 'core/global.enum';
import { isPkdImportJobResponse, isPkdImportSyncResponse } from 'app/owners/utils/ownerImportHelpers';

import { AddOwnerImport } from './AddOwnerImport';
import { AddOwnerImportContainerProps } from './AddOwnerImport.types';

export const AddOwnerImportContainer = ({
  isModalOpen,
  onModalClose,
  onClickBackButton,
}: AddOwnerImportContainerProps) => {
  const [file, setFile] = useState<FileWithPath>();
  const [fileError, setFileError] = useState<string>();
  const [downloadLink, setDownloadLink] = useState('');
  const { formatMessage } = useLocale();
  const { load: loadAddOwnersFromFile, close: closeAddOwnersFromFile } = useLoaderToast('addOwnersFromFile');
  const queryClient = useQueryClient();

  const handleJobComplete = () => {
    queryClient.invalidateQueries([QueryKeyEnum.OWNERS]);
  };

  const handleJobError = (error: string) => {
    toast.error(error || formatMessage({ id: AppMessages['common.error'] }));
  };

  const jobStatus = useJobStatus({
    useGlobalModal: true,
    autoClose: false,
    onComplete: handleJobComplete,
    onError: handleJobError,
    title: formatMessage({ id: AppMessages['owners.addOwner.import.importFile.loader'] }),
    description: formatMessage({ id: AppMessages['owners.addOwner.import.importFile.description'] }),
  });

  const resetUploadErrors = () => {
    setFileError(undefined);
    setDownloadLink('');
  };

  const handleFileImportErrors = ({ response }: ClientError<APIError<ValidateOwnersImportFileErrorResponse>>) => {
    setFile(undefined);

    if (response?.data.type === 'pkds_limit') {
      setFileError(response?.data?.error);
      return;
    }

    createDownloadLink(response?.data?.message);
  };

  const { mutate: onValidateOwnersFromFile, isLoading: isValidatingFile } = useMutation(validateOwnersImportFile, {
    onMutate: resetUploadErrors,
    onSuccess: resetUploadErrors,
    onError: handleFileImportErrors,
  });

  const { mutate: onAddCardFromFile, isLoading: isAddingFile } = useMutation(addOwnersFromFile, {
    onMutate: () =>
      loadAddOwnersFromFile(formatMessage({ id: AppMessages['owners.addOwner.import.importFile.loader'] })),
    onSuccess: (response) => {
      try {
        closeAddOwnersFromFile();

        if (!response) {
          onModalClose();
          return;
        }

        if (isPkdImportJobResponse(response)) {
          jobStatus.startJob(response.jobId);

          onModalClose();
          return;
        }

        if (isPkdImportSyncResponse(response)) {
          if (response.bledyImportu) {
            createDownloadLink(response.bledyImportu);
          }

          queryClient.invalidateQueries([QueryKeyEnum.OWNERS]);

          onModalClose();
          return;
        }

        onModalClose();
      } catch (error) {
        console.error('Error in PKD import success handler:', error);
        onModalClose();
      }
    },
    onError: handleFileImportErrors,
    onSettled: () => closeAddOwnersFromFile(),
  });

  useEffect(() => {
    file && onValidateOwnersFromFile(file);
  }, [file]);

  const createDownloadLink = (uploadErrors?: string) => {
    if (!uploadErrors) return;

    const data = new Blob([uploadErrors], { type: 'text/plain' });

    if (downloadLink !== '') window.URL.revokeObjectURL(downloadLink);

    setDownloadLink(window.URL.createObjectURL(data));
  };

  return (
    <AddOwnerImport
      isModalOpen={isModalOpen}
      onModalClose={onModalClose}
      onClickBackButton={onClickBackButton}
      fileError={fileError}
      setFileError={setFileError}
      setFile={setFile}
      file={file}
      onAddFile={() => file && onAddCardFromFile({ body: file })}
      isValidatingFile={isValidatingFile}
      downloadLink={downloadLink}
      isAddingFile={isAddingFile}
    />
  );
};
